import { useNavigate } from "react-router-dom";
import { FileText, Download, TrendingUp, Receipt } from "lucide-react";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { EmptyState } from "../../../components/ui/EmptyState";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useMyPayslips } from "../../../hooks/usePayslip";
import { useAuth } from "../../../hooks/useAuth";
import { payslipApi, type Payslip } from "../../../lib/payslipApi";
import { formatCurrency, formatPeriod } from "../../../utils/format";

function totalDeductions(p: Payslip): number {
  const govDed = p.deductions.reduce((s, d) => s + d.amount, 0);
  const loanDed = p.loanDeductions.reduce((s, d) => s + d.amount, 0);
  const caDed = (p.cashAdvanceDeductions ?? []).reduce((s, d) => s + d.amount, 0);
  return govDed + loanDed + caDed;
}

export default function PayslipPage() {
  const navigate = useNavigate();
  const { toasts, toast, dismissToast } = useToast();
  const { businesses } = useAuth();
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMyPayslips();

  const payslips = data?.pages.flatMap((p) => p.items) ?? [];
  const showBizLabel = businesses.length > 1;

  // Summary stats — computed from all loaded payslips
  const latestPayslip = payslips[0];
  const finalizedPayslips = payslips.filter((p) => p.status === "FINALIZED");
  const totalNetThisYear = finalizedPayslips
    .filter((p) => p.period.startsWith(new Date().getFullYear().toString()))
    .reduce((s, p) => s + p.netPay, 0);
  const countThisYear = finalizedPayslips.filter((p) =>
    p.period.startsWith(new Date().getFullYear().toString()),
  ).length;

  const handleDownload = async (e: React.MouseEvent, payslip: Payslip) => {
    e.stopPropagation(); // prevent row navigation
    const businessId = payslip.businessId ?? "";
    try {
      const { url } = await payslipApi.downloadMine(businessId, payslip.period);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  const handleRowClick = (payslip: Payslip) => {
    const bizId = encodeURIComponent(payslip.businessId ?? "");
    const period = encodeURIComponent(payslip.period);
    navigate(`/employee/payslip/${bizId}/${period}`);
  };

  return (
    <div className="space-y-5">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* ── Summary bar ───────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2447d9]">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#0f172a]">My Payslips</h1>
            <p className="text-xs text-[#64748b]">Semi-monthly payslip history — click a row to view details</p>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {[0, 1].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-[#f1f5f9] animate-pulse" />
            ))}
          </div>
        ) : payslips.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#eff3ff] border border-[#c7d2fe] px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="h-3.5 w-3.5 text-[#4f46e5]" />
                <p className="text-[10px] font-semibold text-[#4f46e5] uppercase tracking-wide">Latest Net Pay</p>
              </div>
              <p className="text-xl font-bold text-[#2447d9]">
                {latestPayslip ? formatCurrency(latestPayslip.netPay) : "—"}
              </p>
              {latestPayslip && (
                <p className="text-[10px] text-[#6366f1] mt-0.5">{formatPeriod(latestPayslip.period)}</p>
              )}
            </div>
            <div className="rounded-xl bg-[#f0fdf4] border border-[#bbf7d0] px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Receipt className="h-3.5 w-3.5 text-[#16a34a]" />
                <p className="text-[10px] font-semibold text-[#16a34a] uppercase tracking-wide">This Year</p>
              </div>
              <p className="text-xl font-bold text-[#15803d]">{formatCurrency(totalNetThisYear)}</p>
              <p className="text-[10px] text-[#4ade80] mt-0.5 text-[#16a34a]">
                {countThisYear} payslip{countThisYear !== 1 ? "s" : ""} finalized
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {/* ── States ────────────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-5 py-4 text-sm text-[#dc2626]">
          Failed to load payslips. Please refresh the page.
        </div>
      )}

      {!isLoading && !error && payslips.length === 0 && (
        <EmptyState
          icon={<FileText className="h-8 w-8 text-[#94a3b8]" />}
          title="No payslips yet"
          description="Your payslips will appear here once HR processes them."
        />
      )}

      {/* ── Payslip list ──────────────────────────────────────────────────── */}
      {!isLoading && !error && payslips.length > 0 && (
        <div className="space-y-2.5">
          {payslips.map((payslip) => {
            const bizId = payslip.businessId ?? "";
            const bizName = businesses.find((b) => b.businessId === bizId)?.name;
            const ded = totalDeductions(payslip);
            const isDraft = payslip.status !== "FINALIZED";

            return (
              <button
                key={`${bizId}-${payslip.period}`}
                type="button"
                onClick={() => handleRowClick(payslip)}
                className="w-full text-left rounded-xl border border-[#e2e8f0] bg-white shadow-sm hover:border-[#c7d2fe] hover:shadow-md transition-all group"
              >
                {/* Main row */}
                <div className="flex items-center gap-4 px-5 py-4">
                  {/* Icon */}
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#eff3ff] group-hover:bg-[#e0e7ff] transition-colors">
                    <FileText className="h-5 w-5 text-[#2447d9]" />
                  </div>

                  {/* Period + business */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#0f172a] text-sm leading-tight">
                      {formatPeriod(payslip.period)}
                    </p>
                    {showBizLabel && bizName && (
                      <p className="text-xs text-[#94a3b8] mt-0.5">{bizName}</p>
                    )}
                  </div>

                  {/* Net pay — hero number */}
                  <div className="text-right shrink-0">
                    <p className="text-base font-bold text-[#0f172a]">
                      {formatCurrency(payslip.netPay)}
                    </p>
                    <p className="text-[10px] text-[#94a3b8] mt-0.5">net pay</p>
                  </div>

                  {/* Gross + deductions — hidden on small screens */}
                  <div className="hidden sm:block text-right shrink-0 w-28">
                    <p className="text-xs text-[#64748b]">{formatCurrency(payslip.grossPay)}</p>
                    <p className="text-[10px] text-[#94a3b8] mt-0.5">
                      {ded > 0 ? `− ${formatCurrency(ded)} ded.` : "gross pay"}
                    </p>
                  </div>

                  {/* Status — only shown when not finalized */}
                  {isDraft && (
                    <span className="hidden sm:inline-flex shrink-0 items-center rounded-full bg-[#fef9c3] px-2.5 py-0.5 text-xs font-semibold text-[#a16207]">
                      Draft
                    </span>
                  )}

                  {/* PDF download */}
                  {payslip.status === "FINALIZED" && (
                    <button
                      type="button"
                      onClick={(e) => handleDownload(e, payslip)}
                      title="Download PDF"
                      className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#94a3b8] hover:border-[#2447d9] hover:text-[#2447d9] hover:bg-[#eff3ff] transition-colors"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Mobile-only deduction sub-row */}
                {ded > 0 && (
                  <div className="flex sm:hidden items-center gap-2 px-5 pb-3 -mt-1">
                    <p className="text-[10px] text-[#94a3b8]">
                      Gross {formatCurrency(payslip.grossPay)} · Deductions − {formatCurrency(ded)}
                    </p>
                    {isDraft && (
                      <span className="inline-flex items-center rounded-full bg-[#fef9c3] px-2 py-0.5 text-[10px] font-semibold text-[#a16207]">
                        Draft
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}

          {/* Load more */}
          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-lg border border-[#e2e8f0] bg-white px-5 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] disabled:opacity-50 transition-colors"
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
